// @flow

import React, { PureComponent } from 'react';
import styled, { keyframes } from 'styled-components';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { colors, fonts } from 'shared/styleguide';
import Icon from 'shared/components/common/Icon';
import SendAiMessage from 'main-app/mutations/SendAiMessage';
import DeleteAiConversation from 'main-app/mutations/DeleteAiConversation';
import AiMessageReceived from 'main-app/subscriptions/AiMessageReceived';
import { marked } from 'marked';

marked.setOptions({ breaks: true, gfm: true });

/* ==================== Animations ==================== */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.4; }
  40% { opacity: 1; }
`;

/* ==================== Styled Components ==================== */

const FAB = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  height: 44px;
  padding: 0 20px;
  border-radius: 22px;
  background: ${colors.azul};
  color: ${colors.white};
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  ${fonts.headerBold};
  font-size: 15px;
  letter-spacing: 0.5px;
  z-index: 9999;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: #0062cc;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ChatPanel = styled.div`
  position: fixed;
  bottom: 90px;
  right: 24px;
  width: 420px;
  height: min(620px, calc(100vh - 120px));
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${colors.azul};
  color: ${colors.white};
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderTitle = styled.div`
  ${fonts.headerBold};
  color: ${colors.white};
  font-size: 15px;
  line-height: 1.2;
`;

const HeaderTagline = styled.div`
  ${fonts.bodyRegular};
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  line-height: 1.2;
  margin-top: 2px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  background: none;
  border: none;
  color: ${colors.white};
  cursor: pointer;
  padding: 2px;
  opacity: 0.8;
  font-size: 18px;
  line-height: 1;

  &:hover {
    opacity: 1;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${colors.paleGreyTwo};
  -webkit-overflow-scrolling: touch;
`;

const MessageRow = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  max-width: 88%;
  ${(p) => (p.align === 'right' ? 'align-self: flex-end; align-items: flex-end;' : 'align-self: flex-start; align-items: flex-start;')}
`;

const UserBubble = styled.div`
  padding: 8px 12px;
  border-radius: 16px 16px 4px 16px;
  background: ${colors.azul};
  color: ${colors.white};
  ${fonts.bodyRegular};
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(227, 30, 36, 0.2);
`;

const AssistantBubble = styled.div`
  padding: 10px 12px;
  border-radius: 4px 16px 16px 16px;
  background: ${colors.white};
  color: ${colors.charcoalGrey};
  border: 1px solid ${colors.lightBlueGrey};
  border-left: 3px solid ${colors.azul};
  ${fonts.bodyRegular};
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  p {
    margin: 0 0 6px 0;
    white-space: normal;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul, ol {
    margin: 4px 0;
    padding-left: 18px;
  }
  li {
    margin-bottom: 2px;
  }
  code {
    background: ${colors.paleGrey};
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12px;
  }
  strong {
    font-weight: 600;
    color: ${colors.charcoalGrey};
  }
  em {
    color: ${colors.coolGrey};
  }
`;

const TablePreview = styled.div`
  overflow: hidden;
  max-width: 100%;

  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 12px;
    white-space: normal;
  }
  th, td {
    border: 1px solid #e2e5ee;
    padding: 5px 8px;
    text-align: left;
  }
  th {
    background: ${colors.paleGrey};
    font-weight: 600;
  }
  tr:nth-child(even) td {
    background: ${colors.paleGreyThree};
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-left: 3px solid ${colors.azul};
  border-radius: 4px 16px 16px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${colors.coolGrey};
  animation: ${pulse} 1.4s infinite ease-in-out;
  animation-delay: ${(p) => p.delay || '0s'};
`;

const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 12px;
  border-top: 1px solid ${colors.lightBlueGrey};
  background: ${colors.white};
  flex-shrink: 0;
`;

const TextInput = styled.textarea`
  flex: 1;
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Source Sans Pro', -apple-system, sans-serif;
  resize: none;
  min-height: 36px;
  max-height: 100px;
  outline: none;
  line-height: 1.4;

  &:focus {
    border-color: ${colors.azul};
  }

  &::placeholder {
    color: ${colors.coolGrey};
  }
`;

const SendButton = styled.button`
  margin-left: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(p) => (p.disabled ? colors.coolGrey : colors.azul)};
  color: ${colors.white};
  border: none;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #0062cc;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-size: 20px;
  ${fonts.headerBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 8px;
`;

const EmptySubtitle = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.coolGrey};
  font-size: 13px;
  line-height: 1.5;
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const ConversationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  ${fonts.bodyRegular};
  font-size: 13px;
  color: ${colors.charcoalGrey};
  transition: background 0.15s;

  &:hover {
    background: ${colors.paleGrey};
  }
`;

const ConvTitle = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ConvDeleteBtn = styled.button`
  background: none;
  border: none;
  color: ${colors.coolGrey};
  cursor: pointer;
  padding: 2px;
  opacity: 0;
  transition: opacity 0.15s;
  font-size: 14px;

  ${ConversationItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${colors.cherry};
  }
`;

const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  margin: 8px 0;
  border: 1px dashed ${colors.lightBlueGrey};
  border-radius: 8px;
  background: none;
  cursor: pointer;
  ${fonts.smallBold};
  color: ${colors.azul};
  transition: background 0.15s;

  &:hover {
    background: ${colors.paleGrey};
  }
`;

const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 4px 10px;
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 999px;
  background: ${colors.white};
  color: ${colors.azul};
  cursor: pointer;
  ${fonts.smallRegular};
  font-size: 11px;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: ${colors.paleGrey};
    border-color: ${colors.azul};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const ModalCard = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  max-width: 90vw;
  max-height: 80vh;
  overflow: auto;
  padding: 24px;
  position: relative;

  table { border-collapse: collapse; width: 100%; font-size: 13px; }
  th, td { border: 1px solid #d9d9d9; padding: 8px 14px; text-align: left; }
  th { background: #f0f0f0; font-weight: 600; position: sticky; top: 0; }
  tr:nth-child(even) td { background: #fafafa; }
  tr:hover td { background: #e8f0fe; }
  p { margin: 0 0 10px 0; ${fonts.bodyRegular}; font-size: 13px; color: ${colors.charcoalGrey}; }
  p:last-child { margin-bottom: 0; }
  strong { font-weight: 600; }
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${colors.paleGrey};
  color: ${colors.charcoalGrey};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: ${colors.lightBlueGrey};
  }
`;

/* ==================== Component ==================== */

const CONVERSATIONS_QUERY = graphql`
  query AiChatConversationsQuery {
    aiConversations {
      conversationId
      title
      updatedAt
    }
  }
`;

const MESSAGES_QUERY = graphql`
  query AiChatMessagesQuery($conversationId: ID!) {
    aiMessages(conversationId: $conversationId) {
      messageId
      role
      content
      tokensUsed
      createdAt
    }
  }
`;

type Message = {
  messageId: string,
  role: 'user' | 'assistant' | 'system',
  content: string,
  tokensUsed?: number,
  createdAt?: string,
};

type State = {
  open: boolean,
  view: 'chat' | 'conversations',
  conversationId: ?string,
  messages: Array<Message>,
  inputValue: string,
  sending: boolean,
  waitingForReply: boolean,
  expandedHtml: ?string,
};

class AiChat extends PureComponent<{}, State> {
  state = {
    open: false,
    view: 'chat',
    conversationId: null,
    messages: [],
    inputValue: '',
    sending: false,
    waitingForReply: false,
    expandedHtml: null,
  };

  messagesEndRef: any = null;
  textInputRef: any = null;
  subscriptionDisposable: any = null;

  componentWillUnmount() {
    this.disposeSubscription();
  }

  toggleOpen = () => {
    this.setState((prev) => ({ open: !prev.open }));
  };

  scrollToBottom = () => {
    if (this.messagesEndRef) {
      this.messagesEndRef.scrollIntoView({ behavior: 'smooth' });
    }
  };

  setupSubscription = (conversationId: string) => {
    this.disposeSubscription();

    this.subscriptionDisposable = AiMessageReceived.commit({
      variables: { conversationId },
      onNext: (response: any) => {
        if (response && response.aiMessageReceived) {
          const msg = response.aiMessageReceived;
          this.setState((prev) => {
            // Avoid duplicates
            const exists = prev.messages.find((m) => m.messageId === msg.messageId);
            if (exists) return null;

            return {
              messages: [...prev.messages, msg],
              waitingForReply: msg.role.toLowerCase() === 'user',
            };
          }, this.scrollToBottom);
        }
      },
      onError: (err) => {
        console.error('[AiChat] Subscription error:', err);
      },
    });
  };

  disposeSubscription = () => {
    if (this.subscriptionDisposable && this.subscriptionDisposable.dispose) {
      this.subscriptionDisposable.dispose();
    }
    this.subscriptionDisposable = null;
  };

  loadConversation = (conversationId: string, messages: Array<Message> = []) => {
    this.setState(
      {
        conversationId,
        messages,
        view: 'chat',
        waitingForReply: false,
      },
      () => {
        this.setupSubscription(conversationId);
        this.scrollToBottom();
      },
    );
  };

  handleNewChat = () => {
    this.disposeSubscription();
    this.setState({
      conversationId: null,
      messages: [],
      view: 'chat',
      waitingForReply: false,
    });
  };

  handleSend = async () => {
    const { inputValue, conversationId, sending } = this.state;
    const trimmed = inputValue.trim();

    if (!trimmed || sending) return;

    // Optimistically add user message
    const optimisticMsg: Message = {
      messageId: `temp-${Date.now()}`,
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString(),
    };

    this.setState(
      (prev) => ({
        messages: [...prev.messages, optimisticMsg],
        inputValue: '',
        sending: true,
        waitingForReply: true,
      }),
      this.scrollToBottom,
    );

    try {
      const result = await SendAiMessage.commit({
        variables: {
          input: {
            message: trimmed,
            conversationId: conversationId || undefined,
          },
        },
      });

      const data = result.sendAiMessage;
      if (data && data.conversationId && !conversationId) {
        // New conversation created — set it and subscribe
        this.setState({ conversationId: data.conversationId }, () => {
          this.setupSubscription(data.conversationId);
        });
      }
    } catch (e) {
      // Show error as assistant message
      this.setState(
        (prev) => ({
          messages: [
            ...prev.messages,
            {
              messageId: `err-${Date.now()}`,
              role: 'assistant',
              content: `Sorry, something went wrong: ${e.message || 'Unknown error'}`,
            },
          ],
          waitingForReply: false,
        }),
        this.scrollToBottom,
      );
    } finally {
      this.setState({ sending: false });
    }
  };

  handleKeyDown = (e: SyntheticKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };

  handleDeleteConversation = async (conversationId: string, e: Event) => {
    e.stopPropagation();
    try {
      await DeleteAiConversation.commit({
        variables: { input: { conversationId } },
      });

      if (this.state.conversationId === conversationId) {
        this.handleNewChat();
      }
    } catch (err) {
      // Silently handle
    }
  };

  showConversations = () => {
    this.setState({ view: 'conversations' });
  };

  renderAssistantContent(content: string) {
    const trimmed = (content || '').trim();
    if (!trimmed) return null;

    const hasTable = /\|.+\|[\r\n]+\|[-: ]+\|/.test(trimmed);
    const html = marked(trimmed);

    if (hasTable) {
      return (
        <>
          <TablePreview dangerouslySetInnerHTML={{ __html: html }} />
          <ExpandButton onClick={() => this.setState({ expandedHtml: html })}>
            ⤢ View full table
          </ExpandButton>
        </>
      );
    }

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  }

  renderMessages() {
    const { messages, waitingForReply } = this.state;

    if (messages.length === 0) {
      return (
        <EmptyState>
          <EmptyTitle>Axiom</EmptyTitle>
          <EmptySubtitle>
            Ask me about your orders, jobs, inventory,{'\n'}
            or anything about your manufacturing data.
          </EmptySubtitle>
        </EmptyState>
      );
    }

    return (
      <MessagesContainer>
        {messages
          .filter((m) => m.role.toLowerCase() !== 'system')
          .map((msg) => {
            const role = msg.role.toLowerCase();
            if (role === 'assistant') {
              return (
                <MessageRow key={msg.messageId} align="left">
                  <AssistantBubble>
                    {this.renderAssistantContent(msg.content)}
                  </AssistantBubble>
                </MessageRow>
              );
            }
            return (
              <MessageRow key={msg.messageId} align="right">
                <UserBubble>{msg.content}</UserBubble>
              </MessageRow>
            );
          })}
        {waitingForReply && (
          <MessageRow align="left">
            <TypingIndicator>
              <Dot delay="0s" />
              <Dot delay="0.2s" />
              <Dot delay="0.4s" />
            </TypingIndicator>
          </MessageRow>
        )}
        <div ref={(el) => { this.messagesEndRef = el; }} />
      </MessagesContainer>
    );
  }

  renderConversations() {
    return (
      <QueryRenderer
        environment={relayEnvironment}
        query={CONVERSATIONS_QUERY}
        variables={{}}
        render={({ props, error }) => {
          if (error) return <EmptyState><EmptySubtitle>Error loading conversations.</EmptySubtitle></EmptyState>;
          if (!props) return <EmptyState><EmptySubtitle>Loading...</EmptySubtitle></EmptyState>;

          const conversations = props.aiConversations || [];

          return (
            <ConversationList>
              <NewChatButton onClick={this.handleNewChat}>
                + New Chat
              </NewChatButton>
              {conversations.length === 0 && (
                <EmptyState>
                  <EmptySubtitle>No conversations yet.</EmptySubtitle>
                </EmptyState>
              )}
              {conversations.map((conv) => (
                <ConversationItem
                  key={conv.conversationId}
                  onClick={() => this.handleLoadConversation(conv.conversationId)}
                >
                  <ConvTitle>{conv.title || 'Untitled'}</ConvTitle>
                  <ConvDeleteBtn
                    onClick={(e) => this.handleDeleteConversation(conv.conversationId, e)}
                  >
                    ✕
                  </ConvDeleteBtn>
                </ConversationItem>
              ))}
            </ConversationList>
          );
        }}
      />
    );
  }

  handleLoadConversation = (conversationId: string) => {
    // Fetch messages for the conversation then load
    // Use a simple fetch via QueryRenderer re-render
    this.setState({ view: 'loading' }, () => {
      // We'll use the messages query inline
      const { fetchQuery } = require('react-relay');
      fetchQuery(relayEnvironment, MESSAGES_QUERY, { conversationId })
        .then((data) => {
          this.loadConversation(conversationId, data.aiMessages || []);
        })
        .catch(() => {
          this.loadConversation(conversationId, []);
        });
    });
  };

  render() {
    const { open, view, inputValue, sending, expandedHtml } = this.state;

    if (!open) {
      return (
        <FAB onClick={this.toggleOpen} title="Axiom">
          Axiom
        </FAB>
      );
    }

    return (
      <>
        <FAB onClick={this.toggleOpen} title="Close Axiom">
          ✕
        </FAB>
        <ChatPanel>
          <Header>
            <HeaderLeft>
              <HeaderTitle>
                {view === 'conversations' ? 'Conversations' : 'Axiom'}
              </HeaderTitle>
              {view === 'chat' && (
                <HeaderTagline>Your Personal Floor Manager</HeaderTagline>
              )}
            </HeaderLeft>
            <HeaderActions>
              {view === 'chat' && (
                <HeaderButton onClick={this.showConversations} title="History">
                  ☰
                </HeaderButton>
              )}
              {view === 'conversations' && (
                <HeaderButton onClick={() => this.setState({ view: 'chat' })} title="Back to Chat">
                  ←
                </HeaderButton>
              )}
              <HeaderButton onClick={this.handleNewChat} title="New Chat">
                +
              </HeaderButton>
            </HeaderActions>
          </Header>

          {view === 'conversations'
            ? this.renderConversations()
            : this.renderMessages()}

          {view === 'chat' && (
            <InputArea>
              <TextInput
                ref={(el) => { this.textInputRef = el; }}
                value={inputValue}
                onChange={(e) => this.setState({ inputValue: e.target.value })}
                onKeyDown={this.handleKeyDown}
                placeholder="Ask about your orders, jobs, items..."
                rows={1}
              />
              <SendButton
                onClick={this.handleSend}
                disabled={!inputValue.trim() || sending}
                title="Send"
              >
                ➤
              </SendButton>
            </InputArea>
          )}
        </ChatPanel>
        {expandedHtml && (
          <ModalOverlay onClick={() => this.setState({ expandedHtml: null })}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <ModalCloseBtn onClick={() => this.setState({ expandedHtml: null })}>
                ✕
              </ModalCloseBtn>
              <div dangerouslySetInnerHTML={{ __html: expandedHtml }} />
            </ModalCard>
          </ModalOverlay>
        )}
      </>
    );
  }
}

export default AiChat;
